export interface SpellText { name: string; desc: string; evoName: string; evoDesc: string }
export interface NameDesc { name: string; desc: string }
export interface CharText { name: string; title: string; perk: string }
export interface EnemyText { name: string; title?: string }
export interface StageText { name: string; subtitle: string }

const ui = {
  'doc.title': 'Mana Storm',
  'title.tagline': 'Survive the horde. Become the storm.',
  'title.gold': '{n} gold',
  'title.level': 'Level {n}',
  'title.levelXp': '{into} / {need} XP to the next level',
  'title.hintKeys': 'Move with the mouse pointer, WASD or arrows · Space or right-click to blink · Esc to pause',
  'title.hintTouch': 'Drag to move · Tap BLINK to dash · ❚❚ to pause',
  'menu.play': 'Play',
  'menu.shop': 'Grimoire',
  'menu.shopReady': 'You can afford an upgrade',
  'menu.settings': 'Settings',
  'menu.howto': 'How to play',
  'menu.install': 'Install game',
  'install.title': 'Install Mana Storm',
  'install.ios1': 'Tap <b>Share</b> in the Safari toolbar.',
  'install.ios2': 'Choose <b>Add to Home Screen</b>, then <b>Add</b>.',
  'install.ok': 'Got it',
  'menu.language': 'فارسی',
  'common.back': 'Back',
  'common.continue': 'Continue',
  'common.fullscreen': 'Fullscreen',
  'common.exitFullscreen': 'Exit fullscreen',
  'howto.title': 'How to play',
  'howto.1': '<b>Move</b> by pointing with the mouse (the mage follows the pointer), or with WASD or the arrow keys: your spells cast themselves. Positioning is everything.',
  'howto.2': '<b>Collect mana gems</b> to level up and choose 1 of 3 upgrades.',
  'howto.3': '<b>Blink</b> (Space, right-click or the BLINK button) dashes you through enemies with a moment of invulnerability.',
  'howto.4': '<b>Red ground</b> means danger: the fill grows until it strikes.',
  'howto.5': '<b>Elites</b> drop chests. A spell at max level plus its matching passive <b>evolves</b> from a chest.',
  'howto.6': '<b>Braziers</b> hide food, magnets, bombs and freeze charms.',
  'howto.7': '<b>Survive 8:00</b>, then defeat the boss to clear the realm and unlock the next one.',
  'howto.8': 'Gold buys permanent upgrades in the <b>Grimoire</b>.',
  'select.chooseMage': 'Choose your mage',
  'select.chooseRealm': 'Choose a realm',
  'select.begin': 'Begin',
  'select.lockedChar': 'Clear {stage}',
  'select.lockedStage': 'Locked: clear the previous realm',
  'select.best': 'Best: {time}',
  'select.cleared': 'CLEARED',
  'shop.title': 'Grimoire',
  'shop.perRank': '{desc} per rank',
  'shop.maxed': 'MAXED',
  'shop.offline': 'Connect to the internet to buy',
  'menu.armory': 'Armory',
  'armory.title': 'Armory',
  'armory.equipped': 'Equipped',
  'armory.chests': 'Chests',
  'armory.inventory': 'Inventory {n} / {max}',
  'armory.empty': 'Buy a chest to find gear.',
  'armory.full': 'Inventory full: salvage something first',
  'armory.emptySlot': 'Empty',
  'armory.found': 'You found',
  'armory.equip': 'Equip',
  'armory.keep': 'Keep',
  'armory.salvage': 'Salvage ◉ {n}',
  'armory.worn': 'EQUIPPED',
  'armory.tabEquipment': 'Equipment',
  'armory.tabChests': 'Chests',
  'armory.tabMarket': 'Shop',
  'armory.marketHint': 'Pick exactly the piece you want. Its stats are rolled when you buy it.',
  'armory.upTo': 'Up to {stat}',
  'armory.buy': 'Buy',
  'armory.damage': 'Damage +{v}%',
  'armory.maxHp': 'Max HP {v}',
  'armory.level': 'LV {n}',
  'armory.enhance': 'Enhance ◉ {n}',
  'armory.reforge': 'Reforge ◉ {n}',
  'armory.fuse': 'Fuse 3→1 ({rarity})',
  'armory.fuseNeed': 'Fuse 3→1 (Need 3)',
  'armory.maxLevel': 'Max level',
  'armory.salvageCommons': 'Salvage all Commons',
  'armory.keepAll': 'Keep all',
  'armory.x1': 'x1',
  'armory.x10': 'x10',
  'armory.guarantee': '{rarity} or better within {within} pulls · {left} left',
  'armory.guaranteeTop': '{rarity} within {within} pulls · {left} left',
  'armory.needSpace': 'Needs {n} free spaces',
  'armory.next': 'Next level',
  'armory.setProgress': '{name} ({n}/4)',
  'armory.setBonus': '({n}) {stats}',
  'armory.setChip': '{name} ({n})',
  'armory.legendaryPerk': '★ {name}: {desc}',
  'set.ember': 'Ember Set',
  'set.storm': 'Storm Set',
  'set.verdant': 'Verdant Set',
  'set.void': 'Void Set',
  'perk.arc_pulse.name': 'Arcane Pulse',
  'perk.arc_pulse.desc': 'Every 6s, unleashes an arcane wave that damages and knocks back nearby foes',
  'perk.scholar_crown.name': 'Scholar\u2019s Crown',
  'perk.scholar_crown.desc': 'Level-ups always offer 4 choices and runs start with +1 reroll',
  'perk.sanctified_hearth.name': 'Sanctified Hearth',
  'perk.sanctified_hearth.desc': 'Breaking a brazier restores 15 HP and grants +15 gold',
  'perk.aegis_pulse.name': 'Aegis Ward',
  'perk.aegis_pulse.desc': 'Taking damage triggers a shockwave and 1.5s of invulnerability (8s cooldown)',
  'perk.alchemist_loop.name': 'Alchemist\u2019s Loop',
  'perk.alchemist_loop.desc': 'Coins also restore 4 HP and grant +5 XP',
  'perk.stormstep.name': 'Stormstep',
  'perk.stormstep.desc': 'Blink triggers a shockwave that slows foes, and cools down 20% faster',
  'slot.staff': 'Staff',
  'slot.hat': 'Hat',
  'slot.robe': 'Robe',
  'slot.amulet': 'Amulet',
  'slot.ring': 'Ring',
  'slot.boots': 'Boots',
  'rarity.common': 'Common',
  'rarity.rare': 'Rare',
  'rarity.epic': 'Epic',
  'rarity.legendary': 'Legendary',
  'chest.bronze': 'Bronze Chest',
  'chest.silver': 'Silver Chest',
  'chest.gold': 'Gold Chest',
  'gear.might': '+{v}% damage',
  'gear.crit': '+{v}% crit',
  'gear.haste': '-{v}% cooldown',
  'gear.area': '+{v}% area',
  'gear.duration': '+{v}% duration',
  'gear.maxHp': '+{v} max HP',
  'gear.armor': '+{v} armor',
  'gear.regen': '+{v} HP/s',
  'gear.moveSpeed': '+{v}% move speed',
  'gear.magnet': '+{v}% pickup radius',
  'gear.growth': '+{v}% experience',
  'gear.greed': '+{v}% gold',
  'gear.luck': '+{v}% chance of a 4th choice',
  'settings.title': 'Settings',
  'settings.graphics': 'Graphics',
  'settings.quality.auto': 'Auto',
  'settings.quality.low': 'Low',
  'settings.quality.medium': 'Medium',
  'settings.quality.high': 'High',
  'settings.sfx': 'Sound effects',
  'settings.music': 'Music',
  'settings.shake': 'Screen shake',
  'settings.numbers': 'Damage numbers',
  'settings.language': 'Language',
  'settings.controls': 'Touch controls',
  'settings.joystick': 'Joystick',
  'settings.joystick.floating': 'Floating (appears where you touch)',
  'settings.joystick.fixed': 'Fixed (bottom corner)',
  'settings.leftHanded': 'Left-handed layout',
  'settings.haptics': 'Vibration',
  'lang.en': 'English',
  'lang.fa': 'فارسی',
  'hud.level': 'LV {n}',
  'hud.blink': 'BLINK',
  'hud.pause': 'Pause',
  'hud.hp': '{hp} / {max}',
  'hud.max': 'MAX',
  'hud.pace': '{n} kills vs your best run at this time',
  'marker.boss': 'BOSS',
  'marker.elite': 'ELITE',
  'marker.chest': 'CHEST',
  'marker.shrine': 'SHRINE',
  'shrine.arcane.title': 'Arcane Shrine',
  'shrine.arcane.sub': '+35 HP · +35% Might & +25% Haste for 20s',
  'shrine.cursed.title': 'Cursed Altar',
  'shrine.cursed.sub': 'Blood sacrifice! Elites awaken around a Treasure Chest',
  'shrine.obelisk.title': 'Golden Obelisk',
  'shrine.obelisk.sub': '+75 Gold · All experience gems pulled',
  'levelup.title': 'Level {n}',
  'levelup.choose': 'Choose an upgrade',
  'levelup.waiting': '{n} upgrades waiting',
  'levelup.rerolled': 'Rerolled',
  'levelup.reroll': 'Reroll ({n})',
  'card.newSpell': 'NEW SPELL',
  'card.new': 'NEW',
  'card.level': 'Lv {a} → {b}',
  'card.levelMax': 'Lv {a} → MAX',
  'card.evolvesWith': 'Evolves with {name}',
  'card.evolves': 'Evolves {name}',
  'card.evolved': 'EVOLVED',
  'card.bonus': 'BONUS',
  'card.treasure': 'TREASURE',
  'card.goldCache': 'Gold Cache',
  'card.goldCacheDesc': '+25 gold',
  'card.draught': 'Healing Draught',
  'card.draughtDesc': 'Restore 40 HP',
  'card.gold': '{n} Gold',
  'card.goldDesc': 'Added to your purse',
  'chest.evolution': 'Evolution!',
  'chest.evolvedSub': '{name} has evolved',
  'chest.treasure': 'Treasure!',
  'chest.bountiful': 'A bountiful chest',
  'chest.holds': 'The chest holds…',
  'pause.title': 'Paused',
  'pause.sub': '{stage} · {time} · Level {n}',
  'pause.none': 'No passives yet',
  'pause.abandon': 'Abandon run',
  'pause.resume': 'Resume',
  'stat.damage': 'Damage',
  'stat.cooldown': 'Cooldown',
  'stat.area': 'Area',
  'stat.projectiles': 'Projectiles',
  'stat.speed': 'Speed',
  'stat.armor': 'Armor',
  'stat.regen': 'Regen',
  'stat.regenValue': '{v}/s',
  'stat.crit': 'Crit',
  'table.spell': 'Spell',
  'table.level': 'Lv',
  'table.damage': 'Damage',
  'table.dps': 'DPS',
  'table.evo': 'EVO',
  'results.victory': 'Victory',
  'results.defeated': 'Defeated',
  'results.cleared': '{stage} cleared',
  'results.joins': '{name} the {title} joins you',
  'results.slain': 'Slain by {killer} at {time}',
  'results.survived': 'Survived',
  'results.level': 'Level',
  'results.kills': 'Kills',
  'results.gold': 'Gold',
  'results.xp': 'XP',
  'results.mainLevelUp': 'Main level {n}! +{gold} gold · now +{dmg}% damage, +{hp} max HP',
  'results.bossLoot': 'Boss Relic: {rarity} {name}',
  'results.canBuy': 'You can buy {name} ({desc}) in the Grimoire now',
  'results.endless': 'Enter Endless Rift',
  'select.stormTier': 'Storm Tier',
  'select.stormNormal': 'Normal realm intensity',
  'select.stormDesc': 'Enemy HP +{hp}% · Damage +{dmg}% · Gold +{gold}%',
  'banner.endless': 'Endless Rift',
  'banner.endlessSub': 'Survive the escalating storm as long as you can!',
  'shop.tabUpgrades': 'Upgrades',
  'shop.tabEvolutions': 'Evolutions',
  'shop.tabBestiary': 'Bestiary',
  'codex.discovered': 'Discovered',
  'codex.undiscovered': 'Evolve in a run to reveal',
  'codex.slain': 'Slain: {n} / {max}',
  'codex.claim': 'Claim ◉ {n}',
  'codex.claimed': 'Mastered',
  'results.next': 'Next realm',
  'results.again': 'Play again',
  'results.retry': 'Try again',
  'results.menu': 'Main menu',
  'killer.projectile': 'a projectile',
  'killer.ground': 'the ground',
  'killer.curse': 'a curse',
  'killer.rift': 'the rift',
  'killer.sandstorm': 'the sandstorm',
  'killer.rockfall': 'a falling boulder',
  'killer.horde': 'the horde',
  'hud.wave': 'Wave {n}',
  'banner.surrounded': 'Surrounded!',
  'banner.surroundedSub': 'Break through the ring',
  'banner.swarm': 'A swarm approaches',
  'banner.swarmSub': 'Get out of its path',
  'banner.elite': 'An elite approaches',
  'banner.eliteSub': 'It carries a treasure chest',
  'banner.enraged': 'ENRAGED',
  'banner.blizzard': 'Blizzard!',
  'banner.blizzardSub': 'The wind will push you',
  'banner.sandstorm': 'Sandstorm!',
  'banner.sandstormSub': 'The sand pushes and burns',
  'banner.rockfall': 'Rockfall!',
  'banner.rockfallSub': 'The divs hurl boulders',
  'banner.rift': 'A rift tears open',
  'banner.riftSub': 'It pulls everything in',
  'banner.phoenix': 'Phoenix Rises',
  'banner.phoenixSub': 'One more chance',
  'banner.timeStop': 'Time Stop',
  'banner.evolution': 'EVOLUTION',
  'banner.victory': 'VICTORY',
  'banner.fallen': '{name} has fallen',
  'float.gold': '+gold',
  'upg.damage': 'Damage',
  'upg.count': 'Amount',
  'upg.area': 'Area',
  'upg.speed': 'Speed',
  'upg.pierce': 'Pierce',
  'upg.chains': 'Chains',
  'upg.knockback': 'Force',
  'upg.range': 'Range',
  'upg.add': '{v} {label}',
  'upg.cooldown': '{v}s cooldown',
  'upg.duration': '{v}s duration',
  'upg.slow': '{v}% slow',
  'upg.tick': 'Faster ticks',
  'upg.join': ', ',
  'menu.account': 'Sign in',
  'menu.leaderboard': 'Leaderboard',
  'account.title': 'Account',
  'account.signIn': 'Sign in',
  'account.register': 'Register',
  'account.email': 'Email',
  'account.password': 'Password',
  'account.name': 'Player name',
  'account.nameHint': 'Shown on the leaderboard',
  'account.submitSignIn': 'Sign in',
  'account.submitRegister': 'Create account',
  'account.signedInAs': 'Signed in as {name}',
  'account.synced': 'Your progress is saved to your account and synced across devices.',
  'account.guestNote': 'Sign in or create a free account to play. Your progress is saved online and you join the leaderboard.',
  'account.signOut': 'Sign out',
  'account.close': 'Close',
  'account.err.email': 'Enter a valid email address',
  'account.err.password': 'Password must be 8 to 128 characters',
  'account.err.name': 'Name must be 3 to 20 letters, digits or spaces',
  'account.err.emailTaken': 'This email is already registered',
  'account.err.nameTaken': 'This name is already taken',
  'account.err.credentials': 'Wrong email or password',
  'account.err.rate': 'Too many attempts. Try again in a few minutes.',
  'account.err.network': 'Cannot reach the server',
  'account.err.generic': 'Something went wrong',
  'account.err.expired': 'The Telegram sign-in expired. Try again.',
  'account.err.telegram': 'Telegram sign-in is not available right now',
  'account.or': 'or',
  'account.telegram': 'Sign in with Telegram',
  'account.tgWaiting': 'Open our bot in Telegram, tap Start, then tap this number there. This page continues by itself.',
  'account.tgMatch': 'Your number',
  'account.tgOpen': 'Open Telegram',
  'account.tgPending': 'Waiting for confirmation…',
  'account.tgBack': 'Back',
  'account.tgConnect': 'Connect Telegram',
  'account.tgConnectNote': 'Then you can also sign in to this player with Telegram.',
  'account.err.telegramTaken': 'This Telegram account is already used by another player',
  'account.err.alreadyLinked': 'A Telegram account is already connected',
  'leaderboard.title': 'Leaderboard',
  'leaderboard.rank': '#',
  'leaderboard.player': 'Player',
  'leaderboard.kills': 'Kills',
  'leaderboard.time': 'Time',
  'leaderboard.level': 'Level',
  'leaderboard.all': 'All',
  'leaderboard.cleared': 'Realms',
  'leaderboard.you': 'You',
  'leaderboard.empty': 'No runs yet. Be the first!',
  'leaderboard.signInHint': 'Sign in to put your runs on the board.',
  'leaderboard.loading': 'Loading…',
  'leaderboard.offline': 'The leaderboard is unavailable right now.',
  'leaderboard.yourBest': 'Your best: rank {n}',
  'results.submitted': 'Leaderboard rank: {n}',
  'results.newBest': 'New personal best! Leaderboard rank: {n}',
  'results.signInToSubmit': 'Sign in to save runs to the leaderboard',
  'results.notSaved': 'This run could not be saved',
  'results.syncing': 'Saving your rewards…',
  'results.tainted': 'Modified or debug run — not submitted to the leaderboard',
  'reaction.shatter': 'SHATTER!',
  'reaction.superconduct': 'SUPERCONDUCT!',
  'reaction.toxic_ignition': 'TOXIC IGNITION!',
  'reaction.singularity_shock': 'VOID SHOCK!',
  'daily.title': 'Daily Storm Trial',
  'daily.play': 'Play Daily Trial',
  'daily.bounty': 'First run reward: ◉ {n}',
  'daily.claimed': 'Daily bounty claimed ✓',
  'daily.bannerSub': 'Daily Trial · {m1} & {m2}',
  'daily.short': 'Daily Trial',
  'daily.streak': '{n}-day streak',
  'daily.endsIn': 'Ends in {time}',
  'daily.nextIn': 'Done ✓ · next in {time}',
  'leaderboard.daily': 'Daily',
  'results.dailyRank': 'Daily rank: {n}',
  'results.dailyBest': 'Best run today! Daily rank: {n}',
  'mutator.glass_cannon.name': 'Glass Cannon',
  'mutator.glass_cannon.desc': '+60% Damage, -35 Max HP',
  'mutator.hypercharge.name': 'Hypercharge',
  'mutator.hypercharge.desc': '-30% Cooldown, +20% Enemy Speed',
  'mutator.volatile_horde.name': 'Volatile Horde',
  'mutator.volatile_horde.desc': 'Enemies explode on death, +20% Area',
  'mutator.blood_bounty.name': 'Blood Bounty',
  'mutator.blood_bounty.desc': '+75% Gold, +10% Crit, -50% Healing',
  'mutator.swarm_surge.name': 'Swarm Surge',
  'mutator.swarm_surge.desc': '+35% Spawn Rate, +35% XP & Magnet',
  'levelup.banish': 'Banish ({n})',
  'levelup.banished': 'Banished {name}',
  'levelup.lock': 'Lock',
  'levelup.locked': 'Locked ✓',
  'shrine.anvil.title': 'Astral Anvil',
  'shrine.anvil.sub': 'Forged! +1 Spell Level & +25 HP',
  'select.mastery': '★ Rank {n}',
  'results.masteryUp': 'Mage Mastery Rank {n}! +{dmg}% Might & +{hp} Max HP',
  'affix.warlord': 'Warlord',
  'affix.bulwark': 'Bulwark',
  'affix.splitter': 'Splitter',
  'affix.hoarder': 'Hoarder',
};

export type UiKey = keyof typeof ui;

export interface Dict {
  ui: Record<UiKey, string>;
  spells: Record<string, SpellText>;
  passives: Record<string, NameDesc>;
  characters: Record<string, CharText>;
  shop: Record<string, NameDesc>;
  enemies: Record<string, EnemyText>;
  stages: Record<string, StageText>;
  gear: Record<string, { name: string }>;
}

export const en: Dict = {
  ui,
  spells: {
    arcane_bolt: { name: 'Arcane Bolt', desc: 'Homing bolts seek the nearest foes.', evoName: 'Astral Barrage', evoDesc: 'A relentless stream of piercing starbolts.' },
    fireball: { name: 'Fireball', desc: 'Hurls fireballs that explode on impact.', evoName: 'Inferno Comet', evoDesc: 'Comets that scorch the ground they strike.' },
    frost_nova: { name: 'Frost Nova', desc: 'A freezing pulse that slows and repels.', evoName: 'Absolute Zero', evoDesc: 'Freezes everything in a vast radius solid.' },
    chain_lightning: { name: 'Chain Lightning', desc: 'Lightning that leaps between enemies.', evoName: 'Tempest Lord', evoDesc: 'Storms of forking lightning with endless reach.' },
    spirit_orbs: { name: 'Spirit Orbs', desc: 'Orbs that circle you, striking all they touch.', evoName: 'Celestial Halo', evoDesc: 'A permanent ring of radiant spirits.' },
    venom_pool: { name: 'Venom Flask', desc: 'Shatters flasks that leave toxic pools.', evoName: 'Blight Garden', evoDesc: 'Vast, choking bogs that cripple the horde.' },
    radiant_lance: { name: 'Radiant Lance', desc: 'A piercing beam fired where you move.', evoName: 'Solar Judgement', evoDesc: 'Four blinding pillars of sunlight.' },
    wind_scythe: { name: 'Wind Scythe', desc: 'A spinning blade that returns to you.', evoName: 'Tempest Crescent', evoDesc: 'Huge cyclone blades that shred everything.' },
    earth_spike: { name: 'Earth Spike', desc: 'Stone spikes erupt beneath enemies.', evoName: "Titan's Wrath", evoDesc: 'Mountains tear up from the earth.' },
    shadow_dagger: { name: 'Shadow Dagger', desc: 'Rapid daggers thrown where you face.', evoName: 'Thousand Shadows', evoDesc: 'Blades fan out ahead and behind you.' },
    gravity_well: { name: 'Gravity Well', desc: 'A vortex that drags foes in and crushes them.', evoName: 'Event Horizon', evoDesc: 'Collapsing singularities devour the horde.' },
    sunfire_aura: { name: 'Sunfire Aura', desc: 'A searing aura that burns nearby foes and pushes them back.', evoName: 'Solar Corona', evoDesc: 'A miniature sun burns around you.' },
    crystal_shards: { name: 'Crystal Shards', desc: 'Bursts of crystal shards fly out in every direction.', evoName: 'Prism Storm', evoDesc: 'Endless rings of piercing prismatic shards.' },
    blood_siphon: { name: 'Blood Siphon', desc: 'Drains life from the nearest foes to heal you.', evoName: 'Crimson Feast', evoDesc: 'Blood tethers drink deep from the whole horde.' },
    rune_trap: { name: 'Rune Trap', desc: 'Lays runes that explode when foes step on them.', evoName: 'Glyph Field', evoDesc: 'A minefield of runes that shatters the horde.' },
    tidal_wave: { name: 'Tidal Wave', desc: 'A rushing wave that sweeps foes away.', evoName: 'Leviathan Tide', evoDesc: 'Waves crash outward in every direction.' },
  },
  passives: {
    might: { name: 'Arcane Might', desc: '+10% damage' },
    quickening: { name: 'Quickening', desc: '-7% cooldown' },
    expansion: { name: 'Expansion', desc: '+10% area' },
    echo: { name: 'Echo Sigil', desc: '+1 projectile' },
    boots: { name: 'Swift Boots', desc: '+8% move speed' },
    heart: { name: 'Vital Heart', desc: '+20 max HP' },
    lode: { name: 'Lodestone', desc: '+25% pickup radius' },
    tome: { name: "Scholar's Tome", desc: '+8% experience' },
    iron: { name: 'Iron Skin', desc: '+1 armor' },
    bloom: { name: 'Lifebloom', desc: '+0.3 HP/s regen' },
    chrono: { name: 'Chronoglass', desc: '+10% duration' },
    fortune: { name: 'Fortune Coin', desc: '+4% crit, +10% gold' },
    prism: { name: 'Prism Lens', desc: '+5% crit chance' },
    pact: { name: 'Blood Pact', desc: '+6% damage, +10 max HP' },
    runestone: { name: 'Runestone', desc: '+6% area & duration' },
    pearl: { name: 'Moon Pearl', desc: '+8% chance of a 4th choice' },
  },
  characters: {
    aria: { name: 'Aria', title: 'Arcanist', perk: '+10% experience' },
    kael: { name: 'Kael', title: 'Pyromancer', perk: '+10% damage' },
    nyx: { name: 'Nyx', title: 'Shadowblade', perk: '+12% move speed' },
    thora: { name: 'Thora', title: 'Stormcaller', perk: '-8% cooldown' },
    brann: { name: 'Brann', title: 'Earthwarden', perk: '+30 HP, +1 armor' },
    lyra: { name: 'Lyra', title: 'Frostweaver', perk: '+10% area & duration' },
  },
  shop: {
    might: { name: 'Might', desc: '+5% damage' },
    vitality: { name: 'Vitality', desc: '+10 max HP' },
    haste: { name: 'Haste', desc: '-3% cooldown' },
    armor: { name: 'Armor', desc: '+1 armor' },
    regen: { name: 'Recovery', desc: '+0.1 HP/s' },
    speed: { name: 'Swiftness', desc: '+4% move speed' },
    magnet: { name: 'Magnetism', desc: '+10% pickup radius' },
    growth: { name: 'Wisdom', desc: '+5% experience' },
    greed: { name: 'Greed', desc: '+10% gold' },
    area: { name: 'Reach', desc: '+4% area' },
    reroll: { name: 'Insight', desc: '+1 reroll per run' },
    revive: { name: 'Phoenix Ash', desc: 'Revive once per run' },
  },
  gear: {
    ember_staff: { name: 'Ember Staff' },
    thorn_wand: { name: 'Thorn Wand' },
    storm_rod: { name: 'Storm Rod' },
    void_scepter: { name: 'Void Scepter' },
    warded_robe: { name: 'Warded Robe' },
    moss_robe: { name: 'Mossweave Robe' },
    iron_mantle: { name: 'Iron Mantle' },
    shadow_cloak: { name: 'Shadow Cloak' },
    glass_charm: { name: 'Glass Charm' },
    hourglass_pendant: { name: 'Hourglass Pendant' },
    sage_locket: { name: 'Sage Locket' },
    star_talisman: { name: 'Star Talisman' },
    sun_ring: { name: 'Sun Ring' },
    lode_band: { name: 'Lode Band' },
    gilded_ring: { name: 'Gilded Ring' },
    hawk_ring: { name: 'Hawk Ring' },
    pointed_hat: { name: 'Pointed Hat' },
    storm_hood: { name: 'Storm Hood' },
    ember_circlet: { name: 'Ember Circlet' },
    scholar_cap: { name: "Scholar's Cap" },
    wind_striders: { name: 'Wind Striders' },
    iron_greaves: { name: 'Iron Greaves' },
    ember_treads: { name: 'Ember Treads' },
    moss_sandals: { name: 'Moss Sandals' },
  },
  enemies: {
    sproutling: { name: 'Sproutling' },
    gloombat: { name: 'Gloom Bat' },
    thornspitter: { name: 'Thornspitter' },
    mosshulk: { name: 'Moss Hulk' },
    ash_crawler: { name: 'Ash Crawler' },
    cinder_imp: { name: 'Cinder Imp' },
    ember_wisp: { name: 'Ember Wisp' },
    ash_bat: { name: 'Ash Bat' },
    magma_golem: { name: 'Magma Golem' },
    frost_shard: { name: 'Frost Shard' },
    frost_wolf: { name: 'Frost Wolf' },
    snow_owl: { name: 'Snow Owl' },
    frost_wraith: { name: 'Frost Wraith' },
    yeti: { name: 'Yeti' },
    skeleton: { name: 'Skeleton' },
    wailing_ghost: { name: 'Wailing Ghost' },
    necro_acolyte: { name: 'Necro Acolyte' },
    bone_golem: { name: 'Bone Golem' },
    storm_sprite: { name: 'Storm Sprite' },
    harpy: { name: 'Harpy' },
    crag_hound: { name: 'Crag Hound' },
    thunder_shaman: { name: 'Thunder Shaman' },
    stone_giant: { name: 'Stone Giant' },
    void_mite: { name: 'Void Mite' },
    void_splitter: { name: 'Void Splitter' },
    rift_stalker: { name: 'Rift Stalker' },
    abyss_eye: { name: 'Abyss Eye' },
    void_behemoth: { name: 'Void Behemoth' },
    elder_treant: { name: 'Elder Treant', title: 'Warden of the Hollow' },
    molten_colossus: { name: 'Molten Colossus', title: 'Heart of the Ash' },
    frost_matriarch: { name: 'Frost Matriarch', title: 'Queen of the Long Night' },
    lich_king: { name: 'The Lich King', title: 'Lord of the Drowned Dead' },
    thunder_titan: { name: 'Thunder Titan', title: 'Voice of the Storm' },
    hollow_star: { name: 'The Hollow Star', title: 'Hunger Between Worlds' },
    lut_scarab: { name: 'Sand Scarab' },
    lut_vulture: { name: 'Desert Vulture' },
    lut_scorpion: { name: 'Lut Scorpion' },
    desert_ghoul: { name: 'Desert Ghoul' },
    kalut_golem: { name: 'Kalut Golem' },
    div_spawn: { name: 'Div Spawn' },
    forest_raven: { name: 'Forest Raven' },
    div_shaman: { name: 'Div Shaman' },
    div_witch: { name: 'Sorceress' },
    horned_div: { name: 'Horned Div' },
    zahhak_serpent: { name: "Zahhak's Serpent" },
    ash_harpy: { name: 'Ash Harpy' },
    fire_div: { name: 'Fire Div' },
    serpent_charmer: { name: 'Serpent Charmer' },
    magma_div: { name: 'Magma Div' },
    azhdaha: { name: 'Azhdaha', title: 'Serpent of the Burning Sands' },
    white_div: { name: 'Div-e Sepid', title: 'Lord of Mazandaran' },
    zahhak: { name: 'Zahhak', title: 'The Serpent King' },
    brazier: { name: 'Brazier' },
  },
  stages: {
    verdant: { name: 'Verdant Hollow', subtitle: 'A sunlit glade where the trees whisper' },
    ashen: { name: 'Ashen Wastes', subtitle: 'The ground itself remembers fire' },
    frost: { name: 'Frostfang Reach', subtitle: 'Where the wind has teeth' },
    crypt: { name: 'Sunken Crypt', subtitle: 'The drowned dead do not rest' },
    storm: { name: 'Storm Peaks', subtitle: 'Above the clouds, the sky is angry' },
    void: { name: 'Void Rift', subtitle: 'Reality ends here' },
    lut: { name: 'Dasht-e Lut', subtitle: 'The hottest earth under the sun' },
    mazandaran: { name: 'Mazandaran Forest', subtitle: 'Where Rostam faced the divs' },
    damavand: { name: 'Damavand', subtitle: 'The chained king stirs' },
  },
};
